import { CLOSING_TAG, OPENING_TAG } from '@workspace/lib/constants/common';
import {
  SCHEMA_COMMON_PLACEHOLDER_REGEX,
  SCHEMA_PAYLOAD_PLACEHOLDER_REGEX,
} from '@workspace/lib/constants/regex-patterns';
import type { PayloadShape } from '@workspace/lib/types/custom-schema-propeties-definitions';
import type { ValueConversion } from '@workspace/lib/types/form-schema';
import { parseValidationRule } from '@workspace/lib/validation';

/**
 * Transforms form data according to the submit configuration
 * @param submitConfig - Schema submit configuration containing field mappings
 * @param formData - The raw form live data
 * @param conditionalPayloads - Payloads to attach if certain condition is met
 * @returns Transformed data ready for API submission
 */
export function transformFormDataForSubmission<T extends Record<string, any>>(
  payload: PayloadShape,
  formData: T,
  conditionalPayloads?: Array<{ data: PayloadShape; condition: string }>,
): Record<string, any> {
  let transformedData: Record<string, any> = {};

  if (payload) {
    let payloadToProcess = { ...payload };

    if (conditionalPayloads) {
      payloadToProcess = {
        ...payloadToProcess,
        ...attachConditionalPayloads(conditionalPayloads, formData),
      };
    }

    transformedData = insertFormDataToPayload(payloadToProcess, formData);
  }
  return transformedData;
}

/**
 * Processes payload shape to populate it with live form data replacing placeholders
 * @param payload - Payload shape with placeholders
 * @param formData - The raw form live data
 * @returns Processed data with template substitution
 */
function insertFormDataToPayload<T extends Record<string, any>>(
  payload: PayloadShape,
  formData: T,
): Record<string, any> {
  const processedData: Record<string, any> = {};

  Object.entries(payload).forEach(([key, value]) => {
    if (
      typeof value === 'object' &&
      value !== null &&
      !Array.isArray(value) &&
      '_array_transform_' in value &&
      '_template_' in value
    ) {
      const sourceArrayKey = (value._array_transform_ as string).match(
        /\{\{(.*?)\}\}/,
      )?.[1];

      const sourceArray =
        sourceArrayKey && Array.isArray(formData[sourceArrayKey])
          ? (formData[sourceArrayKey] as any[])
          : [];
      const itemTemplate = value._template_ as PayloadShape;

      processedData[key] = sourceArray.map((item) =>
        insertFormDataToPayload(itemTemplate, {
          ...formData,
          ...item,
        }),
      );
    } else if (
      typeof value === 'string' &&
      value.includes(OPENING_TAG) &&
      value.includes(CLOSING_TAG)
    ) {
      // Handle strings
      processedData[key] = substitutePlaceholder(value, formData);
    } else if (Array.isArray(value)) {
      // Handle arrays - process each element recursively
      processedData[key] = value.map((item) => {
        if (typeof item === 'object' && item !== null) {
          return insertFormDataToPayload(item, formData);
        } else if (
          typeof item === 'string' &&
          item.includes(OPENING_TAG) &&
          item.includes(CLOSING_TAG)
        ) {
          return substitutePlaceholder(item, formData);
        } else {
          return item;
        }
      });
    } else if (typeof value === 'object' && value !== null) {
      // Handle nested objects
      processedData[key] = insertFormDataToPayload(
        value as PayloadShape,
        formData,
      );
    } else {
      // Keep the value as-is for non-template values
      processedData[key] = value;
    }
  });

  return processedData;
}

/**
 * Substitutes placeholders in a string with form data values
 * @param template - String containing template placeholders like {{fieldName}}
 * @param formData - The raw form live data
 * @returns Value with placeholders replaced by actual form values (preserves original type)
 */
function substitutePlaceholder<T extends Record<string, any>>(
  template: string,
  formData: T,
): any {
  // If the template is exactly a single placeholder like "{{fieldName}}", return the actual value
  // const singlePlaceholderMatch = template.match(/^\{\{(\w+)\}\}$/);
  const singlePlaceholderMatch = template.match(
    SCHEMA_PAYLOAD_PLACEHOLDER_REGEX,
  );
  if (singlePlaceholderMatch) {
    const matchedValue = singlePlaceholderMatch[1];

    const [fieldName, convertValueTo] = matchedValue.split('|') as [
      string,
      ValueConversion,
    ];

    const fieldValue = formData[fieldName];

    return fieldValue !== undefined
      ? normalizeFormValue(convertValueTo, fieldValue)
      : template;
  }

  // For strings with multiple placeholders or mixed content, do string replacement
  return template.replace(
    SCHEMA_COMMON_PLACEHOLDER_REGEX,
    (match, fieldName) => {
      return formData[fieldName] !== undefined
        ? String(formData[fieldName])
        : match;
    },
  );
}

/**
 * Normalizes a form field value based on the specified conversion type.
 * @param {ValueConversion} convertTo - The target conversion type (e.g. "to_boolean", "to_number", etc.).
 * @param {string | boolean} value - The input value to normalize.
 * @returns {string | number | boolean} The normalized value according to the conversion rules.
 */
function normalizeFormValue(
  convertTo: ValueConversion,
  value: string | boolean,
): string | number | boolean {
  if (typeof value === 'string') {
    if (convertTo === 'to_boolean') {
      return value === 'true' ? true : false;
    }

    if (convertTo === 'to_number') {
      const numValue = Number(value);
      if (isNaN(numValue)) {
        console.warn(`Invalid numeric value: ${value}.`);
      }
      return numValue;
    }

    if (convertTo === 'to_boolean_number') {
      return value === 'true' ? 1 : 2;
    }

    if (convertTo === 'to_phone_number') {
      return value.replace(/\+(40|48)|\s/g, '');
    }

    if (convertTo === 'to_date') {
      const matchFormat = /^\d{4}-\d{2}-\d{2}/.test(value);
      if (!matchFormat) {
        console.warn(
          `Invalid date format: "${value}". Expected ISO format (YYYY-MM-DD).`,
        );
      }
      return value.slice(0, 10);
    }

    if (convertTo === 'to_original') {
      if (typeof value !== 'string') {
        return value;
      }
      return value.trim();
    }
  }

  if (typeof value === 'boolean') {
    if (convertTo === 'to_boolean') {
      return value;
    }

    if (convertTo === 'to_boolean_invert') {
      return !value;
    }
    return value ? 1 : 2;
  }

  if (convertTo === 'to_id_card_number') {
    return value
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .slice(0, 9);
  }

  if (convertTo === 'to_postal_code') {
    return value.replace(/\s+/g, '');
  }

  if (convertTo === 'to_digits_only') {
    return String(value).replace(/\D/g, '');
  }

  return value;
}

/**
 * Consolidates multiple conditional data fragments into a single payload.
 * * It evaluates each condition against the provided form state and merges the
 * associated data if the rule passes.
 * @template T - The shape of the form data used for validation.
 * @param {Array<{ data: PayloadShape; condition: string }>} payloads - An array of data objects and their string-based trigger rules.
 * @param {T} formData - The current form state used to evaluate conditions.
 * @returns {PayloadShape} A merged object containing all payloads that met their conditions.
 */
function attachConditionalPayloads<T extends Record<string, unknown>>(
  payloads: Array<{ data: PayloadShape; condition: string }>,
  formData: T,
) {
  return payloads.reduce((acc, curr) => {
    if (!curr) return acc;

    const shouldAttachPayload = parseValidationRule(curr.condition)(formData);

    if (shouldAttachPayload) {
      return { ...acc, ...curr.data };
    }

    return { ...acc };
  }, {} as PayloadShape);
}
